import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CachedApp {
  id: string;
  user_id: string;
  ad_account_id: string;
  app_id: string;
  app_name: string;
  display_name: string;
  last_synced_at: string | null;
  created_at: string | null;
}

export function useCachedApps(adAccountId: string | null | undefined, options?: { refetchInterval?: number | false | ((query: any) => number | false) }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["cached-apps", userId, adAccountId],
    queryFn: async (): Promise<CachedApp[]> => {
      if (!userId || !adAccountId) return [];

      const { data, error } = await supabase
        .from("meta_cached_apps")
        .select("*")
        .eq("user_id", userId)
        .eq("ad_account_id", adAccountId)
        .order("app_name");

      if (error) throw error;
      return (data ?? []) as CachedApp[];
    },
    enabled: !!userId && !!adAccountId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["cached-apps", userId, adAccountId] });
  };

  return { ...query, invalidate };
}
